import { useMutation, useQuery } from '@apollo/client/react';
import { App } from 'antd';
import { useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { SheetEditorParameterFragment } from '../components/sheet-editor/SheetEditor.gql';
import type { SheetDataWithId } from '../components/sheet-editor/types.ts';
import { getFragmentData } from '../graphql/generated';
import type { SheetData } from '../graphql/sheet.ts';
import { LAB_SHEET_QUERY, SAVE_LAB_SHEET_MUTATION } from './useLabSheets.gql';

export const useLabSheets = (labId: string) => {
  const { message } = App.useApp();
  const [changedSheetData, setChangedSheetData] = useState<SheetDataWithId[] | null>(null);
  const [saveLabSheetsMutation] = useMutation(SAVE_LAB_SHEET_MUTATION);

  const { data, loading } = useQuery(LAB_SHEET_QUERY, {
    variables: {
      labId: labId,
    },
    skip: !labId,
  });

  const sheetData = useMemo(() => {
    // user manipulated the data
    if (changedSheetData) {
      return changedSheetData;
    }

    // initial data from API
    if (data?.labSheet) {
      return (data.labSheet.data as SheetData[]).map(sheet => ({
        ...sheet,
        id: uuidv4(),
        groups: sheet.groups.map(group => ({
          ...group,
          id: uuidv4(),
          parameters: group.parameters.map(param => ({
            ...param,
            paramId: param.id,
            id: uuidv4(),
          })),
        })),
      }));
    }

    // API loading
    return [];
  }, [changedSheetData, data]);

  const saveHandler = async (data: SheetDataWithId[]) => {
    try {
      await saveLabSheetsMutation({
        variables: {
          labId: labId,
          input: {
            data: data.map(it => ({
              name: it.name,
              groups: it.groups.map(it => ({
                name: it.name,
                parameterIds: it.parameters.map(it => it.paramId),
              })),
            })),
          },
        },
      });
    } catch {
      message.error('Das Sheet konnte nicht gespeichert werden');
    }
  };

  const autoSave = (sheets: SheetDataWithId[]) => {
    setChangedSheetData(sheets);
    saveHandler(sheets);
  };

  return {
    autoSave,
    loading,
    sheetData,
    labParameters: data?.labParameters?.filter(it => !getFragmentData(SheetEditorParameterFragment, it).deactivated) ?? [],
  };
};
